import moment from 'moment';
import { buildLeadChatUrl } from 'src/components/ChatModal/LeadChat.functions';
import {
  CALENDAR_CONNECTION_EVENTS,
  eNotificationEvent,
  HANDOFF_EVENTS,
  LEAD_APPOINTMENT_EVENTS,
} from 'src/providers/NotificationProvider/Notification.events';
import type { tEventResponse } from 'src/pages/Agenda/components/Calendar/Calendar.types';
import type {
  tCalendarConnectionNotificationBody,
  tGenericNotification,
  tHandoffUnattendedNotificationBody,
  tLeadHandoffAssignedNotificationBody,
  tLeadAppointmentNotificationBody,
  tNotificationCopy,
} from './AgendaNotifications.types';

export const AGENDA_NOTIFICATION_NS = 'common/alertCenter';

/**
 * A notification is an EVENT WITH A DATE, not a condition: "Damo booked the
 * 4th" never stops being true, so it has no predicate, no exclusivity group
 * and no prominence decay. That is why the tray keeps its own copy resolver
 * instead of entering `AlertRegistry` — see AGI-346.
 *
 * Pure on purpose: everything the row needs is derived here and the panel only
 * renders, so the wire-shape edge cases below are covered by unit tests rather
 * than by clicking through six event types.
 */

/** A wire date we cannot format. `starts_at` is nullable in the contract. */
function readableMoment(raw: string | null | undefined): moment.Moment | null {
  // `moment(undefined)` is NOW, so a missing date would silently render the
  // current time as the appointment's — the toasts this replaces did exactly
  // that. An absent date has to reach the copy resolver as absent.
  if (!raw) return null;
  const parsed = moment(raw);
  return parsed.isValid() ? parsed : null;
}

function appointmentCopy(
  notification: tGenericNotification,
): tNotificationCopy {
  const body = notification.notification as tLeadAppointmentNotificationBody;
  const at = readableMoment(body.starts_at);

  const variant = (() => {
    switch (notification.event) {
      case eNotificationEvent.LEAD_APPOINTMENT_MOVED:
        return 'moved';
      case eNotificationEvent.LEAD_APPOINTMENT_CANCELLED:
        return 'cancelled';
      default:
        return 'created';
    }
  })();

  // The lead's name is the subject of every one of these sentences. An absent
  // name is left EMPTY here and filled by the renderer with
  // `agenda.leadFallback`: the stand-in is itself translated copy, and this
  // function stays free of `t`.
  const values: Record<string, string | number> = {
    name: body.lead_name || '',
    date: at ? at.format('DD/MM') : '',
    time: at ? at.format('hh:mm A') : '',
  };

  return {
    titleKey: `agenda.appointment.${variant}.title`,
    bodyKey: at
      ? `agenda.appointment.${variant}.body`
      : `agenda.appointment.${variant}.bodyNoDate`,
    values,
    // AGI-309's CTA, kept verbatim: the same leadId, the same chat route the
    // backend's own ConversationURL() builds.
    cta: body.lead_id
      ? {
          labelKey: 'agenda.appointment.cta',
          target: buildLeadChatUrl({ leadId: body.lead_id }),
        }
      : null,
    icon: 'appointment',
  };
}

/**
 * AGI-352 — only RESTORED draws a row now. A revocation is a condition, not an
 * event: it stays true until the owner reconnects, so it renders as the
 * `calendar-revoked` ALERT (`AlertRegistry`), whose predicate lives exactly as
 * long as the revocation does. The tray row it replaces was dismissible and
 * never came back, while the Lead Agent went on double-booking — the defect
 * this ticket exists to remove. Dropping the row here also keeps the bell
 * badge honest: the alert and a surviving row would count the same fact twice.
 *
 * Restored KEEPS its row: it is a genuinely dated closing notice ("it broke,
 * it is fine now"), and it is the only trace of an outage that began and ended
 * while the user was away — the alert never renders for those, because by
 * first paint the predicate is already false.
 */
function restoredConnectionCopy(
  notification: tGenericNotification,
): tNotificationCopy {
  const body =
    notification.notification as tCalendarConnectionNotificationBody;

  return {
    titleKey: 'agenda.connection.restored.title',
    bodyKey: 'agenda.connection.restored.body',
    values: { email: body.email || '' },
    // No CTA on purpose: there is nothing left to do once the agent is
    // already back to normal.
    cta: null,
    icon: 'connection',
  };
}

function reminderCopy(notification: tGenericNotification): tNotificationCopy {
  const body = notification.notification as unknown as tEventResponse;
  const at = readableMoment(body.start_date);
  const minutes = body.reminder_time_minutes;

  const bodyKey = (() => {
    if (!at) return 'agenda.reminder.bodyNoDate';
    return minutes
      ? 'agenda.reminder.bodyWithMinutes'
      : 'agenda.reminder.body';
  })();

  return {
    // The toast used the raw event name AS the title, which reads fine alone
    // on screen and badly in a list of six other rows. The name moves into
    // the sentence and the title says what happened, like every other row.
    titleKey: 'agenda.reminder.title',
    bodyKey,
    values: {
      name: body.name || '',
      time: at ? at.format('hh:mm A') : '',
      minutes: minutes ?? 0,
    },
    icon: 'reminder',
    cta: null,
  };
}

/**
 * AGI-951 — both handoff events say "a lead needs a human" and open the same
 * chat. An empty `agent_note` picks the `NoNote` body instead of leaving a
 * hole in the sentence, like `bodyNoDate` above.
 */
function handoffCopy(notification: tGenericNotification): tNotificationCopy {
  const unattended =
    notification.event === eNotificationEvent.SMARTLEADBOT_HANDOFF_UNATTENDED;
  const body = notification.notification as
    tLeadHandoffAssignedNotificationBody | tHandoffUnattendedNotificationBody;
  const reason = body.agent_note?.trim() || '';
  const variant = unattended ? 'unattended' : 'assigned';

  return {
    titleKey: `agenda.handoff.${variant}.title`,
    bodyKey: reason
      ? `agenda.handoff.${variant}.body`
      : `agenda.handoff.${variant}.bodyNoNote`,
    values: {
      reason,
      minutes: unattended
        ? ((body as tHandoffUnattendedNotificationBody).minutes_waiting ?? 0)
        : 0,
    },
    cta: body.lead_id
      ? {
          labelKey: 'agenda.handoff.cta',
          target: buildLeadChatUrl({ leadId: body.lead_id }),
        }
      : null,
    icon: 'handoff',
  };
}

/**
 * Null for an event the tray has no copy for. AGI-309 established that as the
 * safe failure: rendering nothing beats rendering another type's fields, which
 * is how "Invalid date" reached a customer's screen before it.
 */
export function resolveNotificationCopy(
  notification: tGenericNotification,
): tNotificationCopy | null {
  if (!notification?.event) return null;

  if (LEAD_APPOINTMENT_EVENTS.includes(notification.event)) {
    return appointmentCopy(notification);
  }
  // AGI-352 — REVOKED resolves null, the same safe failure as an unknown
  // event: no row is drawn and the badge does not count it. The fact is
  // delivered by the `calendar-revoked` alert instead, which cannot be
  // dismissed away while the connection stays broken.
  if (notification.event === eNotificationEvent.CALENDAR_CONNECTION_REVOKED) {
    return null;
  }
  if (CALENDAR_CONNECTION_EVENTS.includes(notification.event)) {
    return restoredConnectionCopy(notification);
  }
  if (
    notification.event === eNotificationEvent.CALENDAR_EVENT_REMINDER_TRIGGERED
  ) {
    return reminderCopy(notification);
  }
  if (HANDOFF_EVENTS.includes(notification.event)) {
    return handoffCopy(notification);
  }
  return null;
}

/**
 * The rows the tray will actually draw. The badge counts THIS, not the raw
 * provider list: a notification the panel drops for an unknown event would
 * otherwise leave a badge the user cannot empty by opening the panel.
 */
export function renderableNotifications(
  notifications: tGenericNotification[] | null,
): tGenericNotification[] {
  if (!notifications) return [];
  return notifications.filter(
    (notification) => resolveNotificationCopy(notification) !== null,
  );
}
